import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

/**
 * TraceTransportService
 *
 * O ÚNICO ponto do app que faz POST de telemetria. Todos os emitters
 * (GeocodeTraceService, OrderFlowTraceService) passam por aqui.
 *
 * Concentrar o transporte num lugar é o que torna os gates auditáveis: o guard
 * de localhost, o timeout e o try/catch valem para todo emitter presente e
 * futuro, em vez de serem reimplementados (ou esquecidos) em cada um.
 *
 * Usa `HttpClient` direto, sem passar pelo interceptor de auth: telemetria não
 * carrega token nem deve participar do refresh/logout do TokenInterceptor (que,
 * aliás, está registrado duas vezes — app.module.ts:96 e core.module.ts:97).
 */
@Injectable({ providedIn: 'root' })
export class TraceTransportService {
  constructor(private http: HttpClient) { }

  /**
   * Roda sob Cypress?
   *
   * Sob o E2E queremos que o POST SAIA mesmo em localhost, para o
   * `cy.intercept` poder afirmar o payload real (ordem dos steps, ausência de
   * PII). É a única forma de testar o emitter ponta a ponta sem depender da AWS.
   * Mesmo padrão já usado em login.page.ts.
   */
  isCypressRun(): boolean {
    try {
      return typeof window !== 'undefined' && !!(window as any).Cypress;
    } catch (e) {
      return false;
    }
  }

  /**
   * Host de desenvolvimento?
   *
   * Existe um único Stage (`prod`), então o endpoint de telemetria é sempre o de
   * produção. Sem este guard, todo `npm start` de dev gravaria eventos reais no
   * funil — inflando adoção, conversão e contagens com tráfego que não é de
   * cliente nenhum. Os apps reais (web/Electron) nunca rodam em localhost, então
   * o guard não suprime tráfego legítimo.
   */
  isLocalhost(): boolean {
    try {
      if (typeof window === 'undefined' || !window.location) return false;
      const h = window.location.hostname;
      return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || /\.local$/.test(h);
    } catch (e) {
      return false;
    }
  }

  /**
   * Gate de ambiente comum a todos os emitters. Cada emitter aplica o gate de
   * ESCOPO (restaurante-alvo, amostragem) por cima deste.
   *
   * Sob Cypress o gate inteiro é furado — de propósito, e é a única forma de o
   * E2E afirmar o payload real (ordem dos steps, ausência de PII) sem depender
   * da AWS. O gate de ESCOPO continua valendo: o spec precisa configurar um
   * restaurante da coorte para ver evento, então o teste exercita o gate de
   * verdade em vez de contorná-lo.
   */
  canEmit(): boolean {
    if (this.isCypressRun()) return true;
    if (!environment.production) return false;
    if (this.isLocalhost()) return false;
    return true;
  }

  /**
   * POST fire-and-forget. Engole qualquer erro e nunca devolve nada de que o
   * chamador possa depender: telemetria não pode derrubar nem atrasar o
   * checkout. Por isso `timeout`, e por isso nunca `await`.
   */
  send(url: string, envelope: any, timeoutMs = 5000): void {
    try {
      this.http.post(url, envelope)
        .pipe(timeout(timeoutMs))
        .subscribe({ error: () => { /* fire-and-forget */ } });
    } catch (e) {
      // idem: nunca propagar para a página
    }
  }

  /**
   * POST via navigator.sendBeacon, com fallback para send().
   *
   * Para os eventos de SAÚDE (ClientAppError): o HttpClient morre junto com a
   * página — exatamente quando o erro mais importa (crash/navegação). O beacon
   * é entregue pelo browser mesmo após o unload.
   *
   * Blob text/plain de propósito: application/json dispararia preflight CORS
   * que o sendBeacon não faz (e alguns browsers bloqueiam); o ingest da stack
   * faz json.loads do body sem olhar Content-Type.
   *
   * Os MESMOS gates do send() se aplicam — quem chama já passou por canEmit().
   */
  sendBeacon(url: string, envelope: any): void {
    try {
      const nav: any = typeof navigator !== 'undefined' ? navigator : null;
      if (nav && typeof nav.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify(envelope)], { type: 'text/plain' });
        if (nav.sendBeacon(url, blob)) return;
      }
    } catch (e) {
      // beacon indisponível/recusou — cai no transporte normal
    }
    this.send(url, envelope);
  }
}
